import React, { useState, useEffect, useRef } from "react";
import "../../css/exploreStays.css";
import panglao from "../../assets/images/explore-stays-1.png";
import boracay from "../../assets/images/explore-stays-2.png";
import elnido from "../../assets/images/explore-stays-3.png";
import moalboal from "../../assets/images/explore-stays-4.png";
import vigan from "../../assets/images/explore-stays-5.png";
import niseko from "../../assets/images/explore-stays-6.png";
import tagaytay from "../../assets/images/explore-stays-7.png";
import bali from "../../assets/images/explore-stays-8.png";
import { getActivePopularDestinations } from "../../api/popular";


// Fallback images for destinations without images
const fallbackImages = [panglao, boracay, elnido, moalboal, vigan, niseko, tagaytay, bali];

// Empty data structure
const emptyData = {
  All: [],
  Beach: [],
  City: [],
  Mountains: [],
  Family: []
};

const ExploreStays = () => {
  const [activeTab, setActiveTab] = useState("Beach");
  const [data, setData] = useState(emptyData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const gridRef = useRef(null);


  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await getActivePopularDestinations();
        
        if (response.success && response.data && response.data.length > 0) {
          // Transform API data 
          const apiDestinations = response.data.map((dest, index) => ({
            name: dest.title,
            location: dest.location,
            price: dest.price,
            url: dest.url,
            image: dest.images && dest.images.length > 0 
              ? dest.images[0] 
              : fallbackImages[index % fallbackImages.length],
            category: dest.category || 'beach',
            isFromAPI: true
          }));

          // Group destinations by category
          const newData = {
            All: apiDestinations,
            Beach: apiDestinations.filter(dest => dest.category === 'beach'),
            City: apiDestinations.filter(dest => dest.category === 'city'),
            Mountains: apiDestinations.filter(dest => dest.category === 'mountains'),
            Family: apiDestinations.filter(dest => dest.category === 'family')
          };
          setData(newData);
        } else {
          // Set empty data if no API data
          setData(emptyData);
        }
      } catch (err) {
        console.error('Failed to fetch popular destinations:', err);
        setError(err.message);
        // Set empty data on error
        setData(emptyData);
      } finally {
        setLoading(false);
      }
    };

    fetchDestinations();
  }, []);

  // Update animation distance when data changes
  useEffect(() => {
    if (gridRef.current && data[activeTab].length > 0) {
      const cards = gridRef.current.children;
      if (cards.length > 0) {
        // Calculate width of one complete set (half of all cards since we duplicate them)
        const singleSetWidth = (cards.length / 2) * (320); // 300px card width + 20px gap
        
        // Update CSS custom property for animation
        gridRef.current.style.setProperty('--scroll-distance', `-${singleSetWidth}px`);
      }
    }
  }, [data, activeTab]);

  if (loading) {
    return (
      <div className="explore-stays">
        <div className="container">
          <h2>Explore stays in popular destinations</h2>
          <div className="loading-state">
            <p>Loading destinations...</p>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="explore-stays">
      <div className="container">
        <h2>Explore stays in popular destinations</h2>
        <p className="subtitle">
          Average prices based on current calendar month
        </p>
        {error && (
          <div className="error-notice">
            <p>Some destinations may not be up to date. {error}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="tabs">
          {Object.keys(data).map((tab) => (
            <button
              key={tab}
              className={`tab-btn ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Stays Grid */}
        <div className="stays-grid-wrapper">
          <div 
            ref={gridRef}
            className={`stays-grid ${data[activeTab].length === 0 ? 'no-data' : ''}`}
          >
            {data[activeTab].length > 0 ? (
              <>
                {/* First set of cards */}
                {data[activeTab].map((stay, index) => (
                  <div 
                    className={`stay-card ${stay.url && stay.url.trim() !== '' ? 'clickable' : ''}`} 
                    key={`first-${stay._id || stay.id || index}`}
                    onClick={() => {
                      if (stay.url && stay.url.trim() !== '') {
                        window.open(stay.url, '_blank', 'noopener,noreferrer');
                      }
                    }}
                  >
                    <img src={stay.image} alt={stay.name} />
                    <div className="stay-info">
                      <h3>{stay.name}</h3>
                      <p>{stay.location}</p>
                      <span className="price">₱{stay.price}</span>
                      <br></br>
                      <small>avg. nightly price</small>
                    </div>
                  </div>
                ))}
                {/* Duplicate set of cards for seamless loop */}
                {data[activeTab].map((stay, index) => (
                  <div 
                    className={`stay-card ${stay.url && stay.url.trim() !== '' ? 'clickable' : ''}`} 
                    key={`second-${stay._id || stay.id || index}`}
                    onClick={() => {
                      if (stay.url && stay.url.trim() !== '') {
                        window.open(stay.url, '_blank', 'noopener,noreferrer');
                      }
                    }}
                  >
                    <img src={stay.image} alt={stay.name} />
                    <div className="stay-info">
                      <h3>{stay.name}</h3>
                      <p>{stay.location}</p>
                      <span className="price">₱{stay.price}</span>
                      <br></br>
                      <small>avg. nightly price</small>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className="no-destinations">
                <p>No popular {activeTab.toLowerCase()} destinations at the moment.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExploreStays;
